import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { serviceImages } from '../data/images';
import type { ServiceId } from '../types/copy';
interface ServicePanelProps {
  serviceId: ServiceId | null;
  onClose: () => void;
  onBook: (id: ServiceId) => void;
}
export function ServicePanel({
  serviceId,
  onClose,
  onBook
}: ServicePanelProps) {
  const {
    t
  } = useLanguage();
  const closeRef = useRef<HTMLButtonElement>(null);
  const service = t.services.items.find(s => s.id === serviceId) ?? null;
  useEffect(() => {
    if (!serviceId) return;
    const previous = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [serviceId, onClose]);
  return <AnimatePresence data-id="element-323" data-mp="o8jkozq8pyth">
      {service && <div className="fixed inset-0 z-50" data-id="element-324" data-mp="obfhgkqcqhgc">
          <motion.div aria-hidden="true" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="absolute inset-0 bg-ink/50" data-id="element-325" data-mp="ja89olo7li9o" />
          <motion.aside role="dialog" aria-modal="true" aria-labelledby="service-panel-title" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease: [0.32, 0.72, 0, 1]
      }} className="absolute right-0 top-0 flex h-full w-full max-w-[540px] flex-col overflow-y-auto bg-cream" data-id="element-326" data-mp="5jv2ilj567le">
            <div className="relative" data-id="element-327" data-mp="1j4gbxou0ai7w">
              <img src={serviceImages[service.id]} alt={service.alt} className="aspect-[16/10] w-full object-cover" data-id="element-328" data-mp="1o0xbdf6noj0a" />
              <button ref={closeRef} type="button" onClick={onClose} aria-label={t.services.close} className="absolute right-4 top-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-cream text-ink transition-colors duration-150 hover:bg-white" data-id="element-329" data-mp="1xzmiccx0kj1r">
                <XIcon className="h-5 w-5" data-id="element-330" data-mp="1a8h4g3amw0u3" />
              </button>
            </div>

            <div className="flex flex-1 flex-col px-7 py-9 md:px-10" data-id="element-331" data-mp="1f1tp62c6znhk">
              <p className="text-[14px] font-semibold text-violet" data-id="element-332" data-mp="hlmonjorhpmy">{service.label}</p>
              <h2 id="service-panel-title" className="mt-2 font-display text-[28px] font-medium leading-tight text-ink" data-id="element-333" data-mp="mkhe34h8icg1">
                {service.title}
              </h2>
              <p className="mt-5 text-[16.5px] text-muted" data-id="element-334" data-mp="2vsm8frd3sn0a">{service.description}</p>

              <div className="mt-8 border-t border-line pt-6" data-id="element-335" data-mp="2qtsq9lmvg7ts">
                <h3 className="font-display text-[15px] font-semibold text-ink" data-id="element-336" data-mp="2qs7yu4vut505">
                  {t.services.includes}
                </h3>
                <ul className="mt-4 space-y-3" data-id="element-337" data-mp="2gwen2npylfjf">
                  {service.features.map(f => <li key={f} className="flex items-start gap-3 text-[16px] text-charcoal" data-id="element-338" data-mp="2c4muk7n8szqn">
                      <span aria-hidden="true" className="mt-[11px] h-1.5 w-1.5 shrink-0 rotate-45 bg-violet" data-id="element-339" data-mp="283bbkkyvjcrf" />
                      {f}
                    </li>)}
                </ul>
              </div>

              <div className="mt-8 border-t border-line pt-6" data-id="element-340" data-mp="1t6kbe9hdlx0r">
                <h3 className="font-display text-[15px] font-semibold text-ink" data-id="element-341" data-mp="1t72enq2ghbi6">
                  {t.services.idealForLabel}
                </h3>
                <p className="mt-3 text-[16px] text-muted" data-id="element-342" data-mp="22s77fp1uptu">{service.idealFor}</p>
              </div>

              <div className="mt-auto pt-10" data-id="element-343" data-mp="3jja4gx05sfrh">
                <button type="button" onClick={() => onBook(service.id)} className="btn btn-primary w-full" data-id="element-344" data-mp="3jigs9gifky1y">
                  {t.services.book}
                </button>
              </div>
            </div>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}